/**
 * @file: MemberGrid.tsx
 *
 * The room-detail members grid pieces: a member avatar tile and the dashed
 * "add member" / "remove member" action tiles. Presentational only.
 */

import { Typography } from '@euler/app/components/typography';
import { BotAvatar } from '@euler/app/flows/chat/components/BotAvatar';
import { PresenceDot } from '@euler/app/flows/chat/components/PresenceDot';
import { useIsOnline } from '@euler/app/flows/chat/lib/session/hooks/usePresence';
import { Avatar } from '@euler/components/Avatar';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatRoomActiveMember } from '@euler/model/chat';
import { Minus, Plus } from 'lucide-react-native';
import { FC, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const MemberAvatar: FC<{ member: ChatRoomActiveMember }> = memo(
  ({ member }) => {
    const { rpx } = useResponsiveContext();
    // Only the bot member (type 'bot') gets the default 服务助手 glyph —
    // platformUserId is NOT a reliable signal (some staff rows carry 0).
    const isBot = member.type === 'bot';
    // Presence is a human signal — every human member (including the viewer,
    // who is online while viewing) shows a dot; only the bot is skipped (no
    // presence model for it yet).
    const online = useIsOnline(isBot ? null : member);
    return (
      <View
        css={`
          align-items: center;
          margin: 0 ${rpx(8)}px ${rpx(12)}px 0;
          width: ${rpx(56)}px;
        `}
      >
        <View
          css={`
            width: ${rpx(48)}px;
            height: ${rpx(48)}px;
          `}
        >
          {isBot && !member.avatarUrl ? (
            <BotAvatar size={rpx(48)} radius={rpx(8)} />
          ) : (
            <Avatar
              uri={member.avatarUrl ?? undefined}
              name={member.displayName ?? '?'}
              textStyle={{
                fontFamily: FontFamily.NotoSans.Medium,
                fontSize: rpx(18),
                lineHeight: rpx(24),
              }}
              style={{
                width: rpx(48),
                height: rpx(48),
                borderRadius: rpx(8),
              }}
            />
          )}
          {online ? (
            <PresenceDot
              style={{ position: 'absolute', right: -rpx(2), bottom: -rpx(2) }}
            />
          ) : null}
        </View>
        <Typography.Text
          size={rpx(11)}
          color="#6b7280"
          numberOfLines={1}
          style={{
            marginTop: rpx(4),
            textAlign: 'center',
          }}
        >
          {member.displayName}
        </Typography.Text>
      </View>
    );
  },
);

/** A dashed action tile (add / remove member). */
const ActionTile: FC<{ onPress: () => void; icon: 'plus' | 'minus' }> = memo(
  ({ onPress, icon }) => {
    const { rpx } = useResponsiveContext();
    const Glyph = icon === 'plus' ? Plus : Minus;
    return (
      <TouchableOpacity
        onPress={onPress}
        css={`
          align-items: center;
          margin: 0 ${rpx(8)}px ${rpx(12)}px 0;
          width: ${rpx(56)}px;
        `}
      >
        <View
          css={`
            width: ${rpx(48)}px;
            height: ${rpx(48)}px;
            border-radius: ${rpx(8)}px;
            border-width: ${rpx(1.5)}px;
            border-color: #d1d5db;
            border-style: dashed;
            align-items: center;
            justify-content: center;
          `}
        >
          <Glyph size={rpx(20)} color="#9ca3af" />
        </View>
      </TouchableOpacity>
    );
  },
);

export const AddButton: FC<{ onPress: () => void }> = memo(({ onPress }) => (
  <ActionTile onPress={onPress} icon="plus" />
));

export const RemoveButton: FC<{ onPress: () => void }> = memo(({ onPress }) => (
  <ActionTile onPress={onPress} icon="minus" />
));
